import { useCallback, useEffect, useRef } from 'react'
import {
	UseFormGetValues,
	UseFormReset,
	UseFormSetValue
} from 'react-hook-form'

import { UploadFormService } from '@/services/uploadForm.service'

import { IOption } from '@/types/selectOption.interface'
import {
	IFetchUploadForm,
	IUploaderForm,
	TPlatformValue
} from '@/types/uploadForm.interface'

import {
	convertTagsToBackendFormat,
	convertTagsToFormFormat
} from '../utils/tagsTransformers'

interface UseFormDataProps {
	selectedPlatform: TPlatformValue | null
	taskId: string
	uploaderFormGetValues: UseFormGetValues<IUploaderForm>
	uploaderFormSetValue: UseFormSetValue<IUploaderForm>
	getDefaultFormValues: () => IUploaderForm
	clearFormFields: () => void
	uploaderFormReset: UseFormReset<IUploaderForm>
	updatePlatformCompletion: (
		platform: TPlatformValue,
		formData: IUploaderForm
	) => void
	onInitialLoad?: () => void
}

export const useFormData = ({
	selectedPlatform,
	taskId,
	uploaderFormGetValues,
	uploaderFormSetValue,
	getDefaultFormValues,
	clearFormFields,
	uploaderFormReset,
	updatePlatformCompletion,
	onInitialLoad
}: UseFormDataProps) => {
	const prevPlatformRef = useRef<TPlatformValue | null>(null)
	const isInitialMount = useRef(true)

	/**
	 * Converts server response data to form format
	 */
	const convertServerDataToFormFormat = useCallback(
		(platformData: Partial<IUploaderForm>): IUploaderForm => {
			// Convert tags from {name, id}[] to IOption[] format
			let tagsForForm: IOption[] = []
			if (platformData.tags && Array.isArray(platformData.tags)) {
				const tagsFromServer = platformData.tags as Array<{
					name?: string
					id?: string | number
				}>
				tagsForForm = convertTagsToFormFormat(tagsFromServer)
			}

			return {
				title: platformData.title || '',
				description: platformData.description || '',
				location: platformData.location || '',
				categories: platformData.categories || [],
				tags: tagsForForm,
				region: platformData.region || null,
				country: platformData.country || null,
				rights: platformData.rights || '',
				UAERegulatedContent: platformData.UAERegulatedContent || '',
				source: platformData.source || '',
				restrictions: platformData.restrictions || ''
			}
		},
		[]
	)

	/**
	 * Populates form fields from server response
	 */
	const populateFormFromResponse = useCallback(
		(response: IFetchUploadForm | null) => {
			// First, clear all fields to default empty values
			const defaults = getDefaultFormValues()
			clearFormFields()
			uploaderFormReset(defaults)

			// Then populate with data from server if it exists
			if (!selectedPlatform) return

			const platformData = response?.platforms?.[selectedPlatform]
			if (!platformData) {
				const emptyFormData = getDefaultFormValues()
				updatePlatformCompletion(selectedPlatform, emptyFormData)
				return
			}

			// Set region first, then country, to avoid country being reset by useEffect
			const regionValue = platformData.region
			const countryValue = platformData.country

			// Set all fields except region, country, and srt_file first
			Object.keys(platformData).forEach((key: string) => {
				const fieldKey = key as keyof IUploaderForm
				if (
					fieldKey !== 'region' &&
					fieldKey !== 'country' &&
					fieldKey in platformData &&
					platformData[fieldKey] !== undefined
				) {
					let value = platformData[fieldKey]!

					// Convert tags from {name, id}[] to IOption[] format if field is tags
					if (fieldKey === 'tags' && Array.isArray(value)) {
						const tagsFromServer = value as Array<{
							name?: string
							id?: string | number
						}>
						value = convertTagsToFormFormat(
							tagsFromServer
						) as unknown as IUploaderForm['tags']
					}

					uploaderFormSetValue(fieldKey, value)
				}
			})

			// Set region first
			if (regionValue !== undefined) {
				uploaderFormSetValue('region', regionValue)
			}

			// Set country after region (in next tick to ensure region is set first)
			if (countryValue !== undefined) {
				setTimeout(() => {
					uploaderFormSetValue('country', countryValue)
				}, 0)
			}

			// Update completion status after populating form
			setTimeout(() => {
				const currentFormData = uploaderFormGetValues()
				updatePlatformCompletion(selectedPlatform, currentFormData)
			}, 0)
		},
		[
			selectedPlatform,
			uploaderFormSetValue,
			clearFormFields,
			getDefaultFormValues,
			updatePlatformCompletion,
			uploaderFormGetValues,
			uploaderFormReset
		]
	)

	/**
	 * Saves current form data to backend
	 */
	const saveCurrentFormData = useCallback(
		async (platform: TPlatformValue) => {
			try {
				const currentFormData = uploaderFormGetValues()
				const hasData = Object.values(currentFormData).some(value => {
					if (Array.isArray(value)) return value.length > 0
					if (typeof value === 'object' && value !== null) return true
					return value !== '' && value !== null
				})

				if (hasData) {
					// Convert tags from IOption[] to {name, id}[] format
					const tagsForBackend = convertTagsToBackendFormat(
						currentFormData.tags
					)

					const jsonData = {
						platforms: {
							[platform]: {
								...currentFormData,
								tags: tagsForBackend
							}
						},
						is_completed: false
					}
					await UploadFormService.sendFormData(
						taskId,
						jsonData as unknown as IFetchUploadForm
					)
				}
			} catch (error) {
				console.error('Error saving form data on tab switch:', error)
			}
		},
		[taskId, uploaderFormGetValues]
	)

	// Load form data when taskId changes (initial load)
	useEffect(() => {
		if (!taskId) return

		UploadFormService.getFormData(taskId)
			.then(result => {
				populateFormFromResponse(result)

				// Check completion status for all languages
				if (result && selectedPlatform) {
					const responseData = result?.platforms?.[selectedPlatform]
					if (responseData) {
						const formData = convertServerDataToFormFormat(responseData)
						updatePlatformCompletion(selectedPlatform, formData)
					} else {
						updatePlatformCompletion(selectedPlatform, getDefaultFormValues())
					}
				}

				isInitialMount.current = false

				prevPlatformRef.current = selectedPlatform

				onInitialLoad?.()
			})
			.catch(error => {
				if (error?.response?.status !== 404) {
					console.error('Error fetching form data:', error)
				}
				clearFormFields()
				isInitialMount.current = false
				prevPlatformRef.current = selectedPlatform ?? null
			})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [taskId])

	// Handle platform switching
	useEffect(() => {
		if (isInitialMount.current) return

		const prevSource = prevPlatformRef.current

		if (!prevSource) {
			clearFormFields()

			UploadFormService.getFormData(taskId)
				.then(result => {
					populateFormFromResponse(result)

					const responseData = result?.platforms?.[selectedPlatform]
					if (responseData) {
						const formData = convertServerDataToFormFormat(responseData)
						updatePlatformCompletion(selectedPlatform, formData)
					} else {
						updatePlatformCompletion(selectedPlatform, getDefaultFormValues())
					}
				})
				.catch(err => {
					console.error(err)
					clearFormFields()
					updatePlatformCompletion(selectedPlatform, getDefaultFormValues())
				})
				.finally(() => {
					prevPlatformRef.current = selectedPlatform
				})

			return
		}

		if (prevSource === selectedPlatform) return

		clearFormFields()

		saveCurrentFormData(prevSource).then(() => {
			UploadFormService.getFormData(taskId)
				.then(result => {
					populateFormFromResponse(result)

					// Update completion status for all languages after loading
					if (result) {
						const responseData = result?.platforms?.[selectedPlatform]

						if (responseData) {
							const formData = convertServerDataToFormFormat(responseData)
							updatePlatformCompletion(selectedPlatform, formData)
						} else {
							updatePlatformCompletion(selectedPlatform, getDefaultFormValues())
						}
					}
				})
				.catch(error => {
					if (error?.response?.status !== 404) {
						console.error('Error fetching form data:', error)
					}
					clearFormFields()
					const emptyFormData = getDefaultFormValues()
					updatePlatformCompletion(selectedPlatform, emptyFormData)
				})
		})
		// }

		prevPlatformRef.current = selectedPlatform
	}, [
		selectedPlatform,
		taskId,
		saveCurrentFormData,
		populateFormFromResponse,
		clearFormFields,
		updatePlatformCompletion,
		getDefaultFormValues,
		convertServerDataToFormFormat
	])

	// Hook manages side effects internally, no return needed
	// Functions are used within the hook's useEffect hooks
}
